import { Step } from 'signup-constants/Step';
import { isEmailVerificationSignup } from '../app/verificationTypeSelectors';
import { isPostVerification } from '../flow-settings/settingsSelectors';
import { getCompletedSteps, getCurrentStep, getNextSteps, getPostAccountCreationSteps, getPostVerificationSteps, getPreVerificationSteps } from '../store/flow/selectors';
import { getIsActiveAndAssignedToVariant } from '../store/experiments/selectors';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { ExperimentParameterGroup } from '../experiments/ExperimentParameterGroup';
import { getEndpointRetries } from '../store/http-responses/selectors';
import { EndpointKey } from '../http/EndpointKey';
import { getGTKMDataKeys } from '../store/signup-data/selectors';
import { isEndpointStatusFetching } from '../store/http-responses/selectors';
import { getGtkmAnimationState } from '../store/gtkm/selector';
import { GtkmAnimationState } from '../store/gtkm/types';
const UNCOUNTABLE_STEPS = [Step.ExistingUser, Step.LandingPage, Step.ResendEmail, Step.Verification, Step.CodeVerification, Step.HublessNoAccount, Step.IntegrationsAccountCreated];
const BADGE_UNCOUNTABLE_STEPS = [Step.LoadingPage, Step.LandingPage, Step.ResendEmail, Step.Verification, Step.CodeVerification, Step.Password];
const removeUncountableSteps = steps => {
  return steps ? steps.filter(stepKey => UNCOUNTABLE_STEPS.indexOf(stepKey) === -1) : [];
};
export const removeBadgeUncountableSteps = steps => {
  return steps ? steps.filter(stepKey => BADGE_UNCOUNTABLE_STEPS.indexOf(stepKey) === -1) : [];
};
export const getStepNumbering = (state, nextSteps = []) => {
  const preVerification = getPreVerificationSteps(state);
  const postVerification = getPostVerificationSteps(state);
  const postAccountCreation = getPostAccountCreationSteps(state);
  const pre = removeUncountableSteps(preVerification);
  const post = removeUncountableSteps(postVerification);
  const postAccountCreationSteps = removeUncountableSteps(postAccountCreation);
  const previousSteps = removeUncountableSteps(getCompletedSteps(state));
  const previousStepsCount = previousSteps.length;
  nextSteps = removeUncountableSteps(nextSteps);
  const nextStepsCount = nextSteps.length;
  const currentStep = getCurrentStep(state);
  const shouldAddExtra = currentStep ? 1 : 0;
  const postVerificationCount = isPostVerification() ? nextStepsCount + previousStepsCount + shouldAddExtra : post.length + postAccountCreationSteps.length;
  const totalSteps = !isEmailVerificationSignup(state) ? nextStepsCount + previousStepsCount + shouldAddExtra : pre.length + postVerificationCount;
  const currentStepCount = isPostVerification() ? previousStepsCount + pre.length + 1 : previousStepsCount + 1;
  return {
    currentStep: currentStepCount,
    totalSteps
  };
};
export const getBadgeStepNumbering = (state, nextSteps = []) => {
  const completedSteps = removeBadgeUncountableSteps(getCompletedSteps(state));
  const cleanedNextSteps = removeBadgeUncountableSteps(nextSteps);
  const completedStepsCount = completedSteps.length;
  const nextStepsCount = cleanedNextSteps.length;
  const currentStep = completedStepsCount + 1;

  // Add 5 to the total steps to account for the progress badge
  const totalSteps = nextStepsCount + completedStepsCount + 5;
  return {
    currentStep,
    totalSteps
  };
};
export const getACQ173VariantABadgeStepNumbering = (state, nextSteps = []) => {
  const completedSteps = removeBadgeUncountableSteps(getCompletedSteps(state));
  const cleanedNextSteps = removeBadgeUncountableSteps(nextSteps);
  const completedStepsCount = completedSteps.length;
  const nextStepsCount = cleanedNextSteps.length;
  const currentStepCount = completedStepsCount + 1;
  const retries = getEndpointRetries(state, EndpointKey.GTKMCompanyInfo);
  const gtkmDataKeys = getGTKMDataKeys(state);
  const gtkmAnimationState = getGtkmAnimationState(state);
  const isGTKMEndpointFetching = isEndpointStatusFetching(EndpointKey.GTKMCompanyInfo)(state);

  // Add 5 to the total steps to account for the progress badge
  let totalSteps = nextStepsCount + completedStepsCount + 5;

  // add 3 extra steps to the total steps when satisfy the following requirements:
  // 1. before second GTKM API call, and no extra data found except domain in first GTKM API call
  // 2. in domain step, when second GTKM API call is fetching, no extra data found except domain in first GTKM API call
  if (retries === 0 && gtkmDataKeys.length === 1 || retries === 1 && isGTKMEndpointFetching && gtkmDataKeys.length === 1) {
    totalSteps = 3 + totalSteps;
  } else if (gtkmAnimationState === GtkmAnimationState.Started) {
    // when in the domain step, and the animation is started, show complated steps + 3 extra steps behind it + 5 progress badge
    totalSteps = completedStepsCount + 8;
  }
  return {
    currentStep: currentStepCount,
    totalSteps
  };
};
export const getCurrentStepNumbering = state => {
  const nextSteps = getNextSteps(state);
  const isAssignedToACQ172Variants = getIsActiveAndAssignedToVariant(state, ExperimentKeys.ACQ0172, [ExperimentParameterGroup.Variant_A, ExperimentParameterGroup.Variant_B, ExperimentParameterGroup.Variant_C]);
  const isAssignedToACQ173VariantAorC = getIsActiveAndAssignedToVariant(state, ExperimentKeys.ACQ0173, [ExperimentParameterGroup.Variant_A, ExperimentParameterGroup.Variant_C]);
  const isAssignedToACQ173VariantB = getIsActiveAndAssignedToVariant(state, ExperimentKeys.ACQ0173, [ExperimentParameterGroup.Variant_B]);
  if (isAssignedToACQ173VariantAorC) {
    return getACQ173VariantABadgeStepNumbering(state, nextSteps);
  }
  if (isAssignedToACQ172Variants || isAssignedToACQ173VariantB) {
    return getBadgeStepNumbering(state, nextSteps);
  }
  return getStepNumbering(state, nextSteps);
};
export const areTherePostAccountCreationSteps = state => {
  const postAccountCreation = getPostAccountCreationSteps(state);
  return postAccountCreation.length > 0;
};
export const onlyMobileAppPromptStep = state => {
  const postAccountCreation = getPostAccountCreationSteps(state);
  return postAccountCreation.length > 0 && postAccountCreation[0] === Step.MobileAppPrompt;
};